<template>
    <div v-if="stacked" class="widget-stack">
        <div v-for="widget in widgets" :key="widget.id" class="widget-stack__item">
            <component :is="resolveComponent(widget.type)" :filters="filters" :widget-data="widget" />
        </div>
    </div>
    <div v-else ref="gridContainer" :class="{ 'grid-stack-editable': editable }" class="grid-stack">
        <div
            v-for="widget in widgets"
            :key="widget.id"
            :gs-h="widget.h"
            :gs-id="widget.id"
            :gs-w="widget.w"
            :gs-x="widget.x"
            :gs-y="widget.y"
            class="grid-stack-item"
        >
            <div class="grid-stack-item-content">
                <component :is="resolveComponent(widget.type)" :filters="filters" :widget-data="widget" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { GridStack } from 'gridstack';
import ProInsightWidget from '@/Pages/DashboardV2/Widgets/ProInsightWidget.vue';
import AssetStatusOverviewWidget from '@/Pages/DashboardV2/Widgets/AssetStatusOverviewWidget.vue';
import AssetTypeDistributionWidget from '@/Pages/DashboardV2/Widgets/AssetTypeDistributionWidget.vue';
import AlertMonitorContainerWidget from '@/Pages/DashboardV2/Widgets/AlertMonitor/AlertMonitorContainerWidget.vue';
import CriticalLocationsWidget from '@/Pages/DashboardV2/Widgets/CriticalLocationsWidget.vue';
import OperationalAvailabilityWidget from '@/Pages/DashboardV2/Widgets/OperationalAvailabilityWidget.vue';
import MeanDowntimeWidget from '@/Pages/DashboardV2/Widgets/MeanDowntimeWidget.vue';
import MeanTimeBetweenCriticalFailureWidget from '@/Pages/DashboardV2/Widgets/MeanTimeBetweenCriticalFailureWidget.vue';
import AssetStatusOverTimeWidget from '@/Pages/DashboardV2/Widgets/AssetStatusOverTimeWidget.vue';
import TestingComplianceByAssetTypeWidget from '@/Pages/DashboardV2/Widgets/TestingComplianceByAssetTypeWidget.vue';
import TestingComplianceShiftWidget from '@/Pages/DashboardV2/Widgets/TestingComplianceShiftWidget.vue';
import TestingComplianceByLineWidget from '@/Pages/DashboardV2/Widgets/TestingComplianceByLineWidget.vue';
import PreventativeMaintenanceWidget from '@/Pages/DashboardV2/Widgets/PreventativeMaintenanceWidget.vue';

const props = defineProps({
    editable: {
        type: Boolean,
        default: false,
    },
    widgets: {
        type: Array,
        required: true,
        default: () => [],
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
    // Opt-out of GridStack's absolute-positioned fixed-height grid — renders
    // widgets in document flow as a vertical stack so each can expand to its
    // own content height instead of scrolling inside a fixed cell.
    stacked: {
        type: Boolean,
        default: false,
    },
    options: {
        type: Object,
        default: () => ({
            cellHeight: 83,
            margin: 10,
            column: 12,
            float: true,
            disableResize: true,
            acceptWidgets: true,
            removable: true,
            staticGrid: true,
            animate: true,
            draggable: {
                handle: '.grid-stack-item-content',
            },
            columnOpts: {
                breakpointForWindow: true,
                breakpoints: [
                    { w: 600, c: 1 },
                    { w: 800, c: 2 },
                    { w: 991, c: 3 },
                    { w: 3000, c: 12 },
                ],
            },
        }),
    },
});

const emit = defineEmits(['update:widgets', 'change']);
const gridContainer = ref(null);

// Component resolver
const componentMap = {
    'pro-insight': ProInsightWidget,
    'asset-status-overview': AssetStatusOverviewWidget,
    'asset-type-distribution': AssetTypeDistributionWidget,
    'alert-monitor': AlertMonitorContainerWidget,
    'critical-locations': CriticalLocationsWidget,
    'operational-availability': OperationalAvailabilityWidget,
    'mean-downtime': MeanDowntimeWidget,
    'mean-time-between-critical-failure': MeanTimeBetweenCriticalFailureWidget,
    'asset-status-over-time': AssetStatusOverTimeWidget,
    'testing-compliance-by-asset-type': TestingComplianceByAssetTypeWidget,
    'testing-compliance-shift': TestingComplianceShiftWidget,
    'testing-compliance-by-line': TestingComplianceByLineWidget,
    'preventative-maintenance-schedule': PreventativeMaintenanceWidget,
};

const resolveComponent = (type) => componentMap[type] || null;

let grid = null;

provide('resizeWidget', (el, h) => grid?.update(el, { h }));

const initGrid = () => {
    grid = GridStack.init(props.options, gridContainer.value);

    grid.on('change', () => {
        const items = grid.getGridItems().map((item) => {
            const node = item.gridstackNode;
            // Find the original widget to preserve its properties
            const originalWidget = props.widgets.find((w) => w.id === node.id);

            return {
                ...originalWidget, // Preserve all original widget properties
                id: node.id,
                x: node.x,
                y: node.y,
                w: node.w,
                h: node.h,
            };
        });

        emit('update:widgets', items);
        emit('change', items);
    });

    grid.setStatic(!props.editable);
};

watch(
    () => props.editable,
    (value) => grid?.setStatic(!value),
    { immediate: true },
);

onMounted(() => {
    if (!props.stacked) {
        initGrid();
    }
});
onBeforeUnmount(() => grid?.destroy());
</script>

<style scoped>
.widget-stack {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.widget-stack__item {
    width: 100%;
}

/* A widget that conditionally renders nothing (e.g. the Shift Compliance card
   when no shifts are configured) leaves its stack item with no element child —
   collapse it so the column gap doesn't leave a hole. */
.widget-stack__item:not(:has(> *)) {
    display: none;
}
</style>
